
import { Component, Vue, Prop } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import store from "../../store/store";
import { MessageTranslationDto, MessageTranslationTextDto } from "../../dtos/messageTranslationDto";
import rules from "@/store/rules";

// Languages the customer-facing messages can be maintained in. Matches the locales
// supported by the Kundenportal client (supportedLocales in its i18n.ts) and the
// WerkstoffBezeichnung management (Werkstoffe.vue). de-CH is the fallback language.
const languages = [
	{ code: "de-CH", text: "Deutsch" },
	{ code: "fr-FR", text: "Französisch" },
	{ code: "en-GB", text: "Englisch" },
	{ code: "it-CH", text: "Italienisch" },
];

const fallbackLanguage = "de-CH";

interface EditText {
	titel: string;
	message: string;
}

@Component
export default class CustomerDashboard extends Vue {
	private loading: boolean = false;
	private saving: boolean = false;
	private messages: MessageTranslationDto[] = [];
	private currentEdit: MessageTranslationDto = new MessageTranslationDto();
	private languages = languages;
	// Per-language titel / message while the edit dialog is open, keyed by language code.
	// Initialised with an (empty) entry for every language so the template's
	// editTexts[code].titel bindings are safe before the dialog is first opened.
	private editTexts: Record<string, EditText> = languages.reduce(
		(map, language) => {
			map[language.code] = { titel: "", message: "" };
			return map;
		},
		{} as Record<string, EditText>
	);
	private languageTab: any = null;
	private messageTypes: any = [];
	private severities: any = [];
	private suchbegriff: string = "";
	private activeTab: any = null;
	private tabs: string[] = ["CAM", "Blexon", "Boost"];
	private editDialog: boolean = false;
	private valid: boolean = false;
	private boostKeyRules: any = [
		(v: string) => !!v || "Angabe erforderlich",
		(v: string) =>
			v.replace(/\s/g, "").length > 0 || "Eingabe darf nicht nur Leerzeichen sein.",
		(v: string) => !v || v.length <= 300 || "Maximal 300 Zeichen erlaubt.",
		(v: string) => !this.boostKeyIsTaken(v) || "BoostKey schon vergeben",
	];
	private requiredRule: any = rules.requiredRule;
	private optionalUrlRule: any = rules.optionalUrlRule;

	private mounted() {
		this.loadCamMessages();
		this.loadMessageTypes();
		this.loadSeverities();
	}

	private get filteredMessages(): MessageTranslationDto[] {
		const lowerSearchTerm = this.suchbegriff.toLowerCase();
		return this.messages.filter(
			(m: any) =>
				m.boostKey.toLowerCase().includes(lowerSearchTerm) ||
				(m.newMessageTitle && m.newMessageTitle.toLowerCase().includes(lowerSearchTerm)) ||
				this.getMessageTypeName(m.messageType).toLowerCase().includes(lowerSearchTerm)
		);
	}

	private getTypedMessages(type: string): MessageTranslationDto[] {
		switch (type) {
			case "Blexon":
				return this.filteredMessages.filter(
					(m: MessageTranslationDto) => m.messageType == 105
				); // 105 = MessageType.Blexon
			case "Boost":
				return this.filteredMessages.filter(
					(m: MessageTranslationDto) => m.messageType == 100
				); // 100 = MessageType.Boost
			case "CAM":
				return this.filteredMessages.filter(
					(m: MessageTranslationDto) => m.messageType !== 105 && m.messageType != 100
				);
			default:
				return [];
		}
	}

	private get isEditMessage(): boolean {
		return this.currentEdit && this.currentEdit.id > 0;
	}
	private boostKeyIsTaken(key: string) {
		return this.messages.some((m: any) => m.boostKey === key && m.id !== this.currentEdit.id);
	}

	private getMessageTypeName(type: number) {
		return this.messageTypes.filter((mt: any) => mt.item2 === type)[0].item1;
	}
	private getTypeFromTab(tab: string): number {
		switch (tab) {
			case "CAM":
				return 0;
			case "Blexon":
				return 105;
			case "Boost":
				return 100;
			default:
				return 0;
		}
	}

	// Validation rules for a language's fields: the German fallback is required (except for
	// Boost messages, whose title / message are derived), all other languages are optional.
	private textRules(languageCode: string): any {
		return languageCode === fallbackLanguage && this.currentEdit.messageType != 100
			? this.requiredRule
			: [];
	}

	// Builds the per-language edit map from the DTO's texts array, with an entry for every
	// supported language so the v-model bindings are reactive.
	private buildEditTexts(texts: MessageTranslationTextDto[]): Record<string, EditText> {
		return this.languages.reduce(
			(map, language) => {
				const text = (texts || []).find((t) => t.languageCode === language.code);
				map[language.code] = {
					titel: text ? text.titel : "",
					message: text ? text.message : "",
				};
				return map;
			},
			{} as Record<string, EditText>
		);
	}

	private async createOrEdit(messageId: number, tab: string) {
		if (messageId > 0) {
			// Edit message
			this.currentEdit = JSON.parse(
				JSON.stringify(
					this.messages.filter((m: MessageTranslationDto) => m.id === messageId)[0]
				)
			);
		} else {
			// Create new message
			this.currentEdit = {
				id: 0,
				messageType: this.getTypeFromTab(tab),
				newSeverity: 0,
				ignore: false,
				ignoreFirstTime: false,
				boostKey: "EINDEUTIGER BEZEICHNER",
				errorCode: "",
				newMessageTitle: "",
				newMessage: "",
				helpLink: "",
				texts: [],
			};
		}
		this.editTexts = this.buildEditTexts(this.currentEdit.texts);
		this.languageTab = 0;
		this.editDialog = true;
		(this.$refs.form as any).validate();
	}

	private async saveMessage() {
		try {
			this.saving = true;
			// Collect the per-language texts, keeping only languages that actually have content.
			this.currentEdit.texts = this.languages
				.map((language) => ({
					languageCode: language.code,
					titel: (this.editTexts[language.code].titel || "").trim(),
					message: (this.editTexts[language.code].message || "").trim(),
				}))
				.filter((text) => text.titel !== "" || text.message !== "");
			// Keep the deprecated German columns in sync for the overview and as a fallback.
			const german = this.currentEdit.texts.find((t) => t.languageCode === fallbackLanguage);
			this.currentEdit.newMessageTitle = german ? german.titel : "";
			this.currentEdit.newMessage = german ? german.message : "";

			const response = await httpClient().post(
				`Cam/SaveMessageTranslation`,
				this.currentEdit
			);
			if (!this.isEditMessage) {
				this.currentEdit.id = response.data;
				this.messages.unshift(JSON.parse(JSON.stringify(this.currentEdit)));
			} else {
				let index = this.messages.findIndex(
					(m: MessageTranslationDto) => m.id === this.currentEdit.id
				);
				this.messages.splice(index, 1, JSON.parse(JSON.stringify(this.currentEdit)));
			}
			this.$store.commit("setSnackbarText", "Meldung gespeichert");
		} catch (ex) {
			console.log(ex);
			this.$store.commit("setSnackbarErrorText", "Speichern der Meldung fehlgeschlagen");
		} finally {
			this.saving = false;
			this.editDialog = false;
		}
	}

	private async deleteMessage(messageId: number) {
		try {
			await httpClient().post(`Cam/DeleteMessageTranslation?messageId=${messageId}`, null);
			let index = this.messages.findIndex((m: MessageTranslationDto) => m.id === messageId);
			this.messages.splice(index, 1);
			this.$store.commit("setSnackbarText", "Meldung gelöscht");
		} catch (ex) {
			console.log(ex);
			this.$store.commit("setSnackbarErrorText", "Meldung konnte nicht gelöscht werden");
		}
	}

	private async loadCamMessages() {
		try {
			this.loading = true;
			const response = await httpClient().get(`Cam/GetMessageTranslations`);
			this.messages = response.data;
		} catch {
			console.log("Load cam messages failed.");
		} finally {
			this.loading = false;
		}
	}

	private async loadMessageTypes() {
		try {
			const response = await httpClient().get(`Cam/GetMessageTypes`);
			this.messageTypes = response.data;
		} catch {
			console.log("Load messages types failed.");
		}
	}

	private async loadSeverities() {
		try {
			const response = await httpClient().get(`Cam/GetSeverities`);
			this.severities = response.data;
		} catch {
			console.log("Load severities failed.");
		}
	}
}
